// src/pages/RestaurantPOS/manager/SalesSummary.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  memo,
} from 'react';

// MUI Core
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Fade,
  Badge,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  Divider,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Popover,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from '@mui/material';

// MUI Icons
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterAlt as FilterAltIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  TableRestaurant as TableRestaurantIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  DateRange as DateRangeIcon,
  Close as CloseIcon,
  AttachMoney as AttachMoneyIcon,
  ReceiptLong as ReceiptLongIcon,
  Today as TodayIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as ShowChartIcon,
  GetApp as GetAppIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';

// Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  RadialLinearScale,
  PolarAreaController,
} from 'chart.js';

import { Bar, Line, PolarArea, Radar } from 'react-chartjs-2';

// Components - THESE MUST BE BEFORE ChartJS.register()
import Header from '../../../components/RestaurantPOS/manager/Header';
import Footer from '../../../components/RestaurantPOS/manager/Footer';
import Preloader from '../../../theme/Preloader';

// ============================================
// Register ChartJS components - AFTER all imports
// ============================================
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  RadialLinearScale,
  PolarAreaController
);


// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;
const ITEMS_PER_PAGE = 10;

const LOCATIONS = [
  { id: 5, name: 'African', color: '#2E7D32', icon: '🌍' },
  { id: 3, name: 'Continental', color: '#1565C0', icon: '🌐' },
  { id: 4, name: 'Lounge', color: '#6A1B9A', icon: '🍸' },
];

const STATUS_CONFIG = {
  PENDING: { label: 'Pending', color: '#FF9800', bg: '#FFF3E0' },
  IN_PROGRESS: { label: 'In Progress', color: '#2196F3', bg: '#E3F2FD' },
  READY: { label: 'Ready', color: '#4CAF50', bg: '#E8F5E9' },
  COMPLETED: { label: 'Completed', color: '#2E7D32', bg: '#E8F5E9' },
  CANCELLED: { label: 'Cancelled', color: '#D32F2F', bg: '#FFEBEE' },
};

const PAYMENT_STATUS_CONFIG = {
  UNPAID: { label: 'Unpaid', color: '#F44336', bg: '#FFEBEE' },
  PAID: { label: 'Paid', color: '#4CAF50', bg: '#E8F5E9' },
  PARTIAL: { label: 'Partial', color: '#FF9800', bg: '#FFF3E0' },
  REFUNDED: { label: 'Refunded', color: '#9E9E9E', bg: '#F5F5F5' },
};

const ORDER_TYPE_CONFIG = {
  DINE_IN: { label: 'Dine In', icon: <TableRestaurantIcon /> },
  TAKEAWAY: { label: 'Takeaway', icon: <PeopleIcon /> },
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (!amount && amount !== 0) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const formatDateInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForAPI = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getStatusColor = (status) => STATUS_CONFIG[status]?.color || '#9E9E9E';
const getStatusLabel = (status) => STATUS_CONFIG[status]?.label || status || 'Unknown';
const getPaymentStatusColor = (status) => PAYMENT_STATUS_CONFIG[status]?.color || '#9E9E9E';
const getPaymentStatusLabel = (status) => PAYMENT_STATUS_CONFIG[status]?.label || status || 'Unknown';
const getOrderTypeLabel = (type) => ORDER_TYPE_CONFIG[type]?.label || type || 'Unknown';

// ============================================
// AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    return !decoded.exp || decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[SalesSummary] Token expiry check error:', err);
    return true;
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[SalesSummary] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[SalesSummary] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[SalesSummary] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in response');
  } catch (err) {
    console.error('[SalesSummary] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');

  if (!token || isTokenExpired(token)) {
    console.debug('[SalesSummary] Token invalid, refreshing...');
    token = await refreshAuthToken();
    if (!token) throw new Error('Authentication required');
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };

  let url = `${FULL_API_URL}${endpoint}`;

  if (options.params) {
    const params = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
  }

  console.debug(`[SalesSummary] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      console.debug('[SalesSummary] Received 401, retrying with new token...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers.Authorization = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          return contentType?.includes('application/json')
            ? await retryResponse.json()
            : { message: await retryResponse.text() };
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      data = { message: await response.text() };
    }

    if (!response.ok) {
      throw new Error(data.detail || data.message || `HTTP ${response.status}`);
    }

    return data;
  } catch (err) {
    console.error('[SalesSummary] API Error:', err.message);
    throw err;
  }
};

// ============================================
// CHART COMPONENTS
// ============================================

const PolarChart = memo(({ data, locationColor, title, height = 250 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: isMobile ? 'bottom' : 'right',
          labels: {
            font: { size: isMobile ? 10 : 12, weight: '700', family: '"Inter", sans-serif' },
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 12,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(255,255,255,0.95)',
          titleColor: '#1a1a1a',
          bodyColor: '#333',
          borderColor: alpha(locationColor, 0.2),
          borderWidth: 1,
          cornerRadius: 12,
          padding: 12,
          titleFont: { weight: '700', family: '"Inter", sans-serif' },
          bodyFont: { weight: '600', family: '"Inter", sans-serif' },
        },
      },
      scales: {
        r: {
          grid: { color: alpha('#000', 0.06) },
          ticks: { font: { size: 10, weight: '600', family: '"Inter", sans-serif' } },
          pointLabels: {
            font: { size: isMobile ? 10 : 12, weight: '700', family: '"Inter", sans-serif' },
          },
        },
      },
    }),
    [isMobile, locationColor]
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${alpha(locationColor, 0.08)}`,
        height: '100%',
        minHeight: 280,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: `0 8px 32px ${alpha(locationColor, 0.1)}`,
          borderColor: alpha(locationColor, 0.2),
        },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${locationColor} 0%, ${alpha(locationColor, 0.3)} 100%)`,
        }}
      />
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mb: 2.5,
            color: locationColor,
            fontSize: { xs: '0.9rem', sm: '1.1rem' },
            fontFamily: '"Inter", sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 20,
              bgcolor: locationColor,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          {title}
        </Typography>
        <Box sx={{ height: isMobile ? 180 : height }}>
          <PolarArea data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
});

const GradientAreaChart = memo(({ data, locationColor, title, height = 250 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            font: { size: isMobile ? 10 : 12, weight: '700', family: '"Inter", sans-serif' },
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 16,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(255,255,255,0.95)',
          titleColor: '#1a1a1a',
          bodyColor: '#333',
          borderColor: alpha(locationColor, 0.2),
          borderWidth: 1,
          cornerRadius: 12,
          padding: 12,
          titleFont: { weight: '700', family: '"Inter", sans-serif' },
          bodyFont: { weight: '600', family: '"Inter", sans-serif' },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: alpha('#000', 0.06), drawBorder: false, drawTicks: false },
          ticks: {
            font: { size: isMobile ? 9 : 11, weight: '600', family: '"Inter", sans-serif' },
            color: '#666',
            padding: 8,
            callback: (value) => '₦' + value.toLocaleString(),
          },
        },
        x: {
          grid: { display: false },
          ticks: {
            font: { size: isMobile ? 8 : 10, weight: '600', family: '"Inter", sans-serif' },
            color: '#666',
            maxRotation: isMobile ? 45 : 0,
            padding: 6,
          },
        },
      },
    }),
    [isMobile]
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${alpha(locationColor, 0.08)}`,
        height: '100%',
        minHeight: 280,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: `0 8px 32px ${alpha(locationColor, 0.1)}`,
          borderColor: alpha(locationColor, 0.2),
        },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${locationColor} 0%, ${alpha(locationColor, 0.3)} 100%)`,
        }}
      />
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mb: 2.5,
            color: locationColor,
            fontSize: { xs: '0.9rem', sm: '1.1rem' },
            fontFamily: '"Inter", sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 20,
              bgcolor: locationColor,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          {title}
        </Typography>
        <Box sx={{ height: isMobile ? 180 : height }}>
          <Line data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
});

const HorizontalBarChart = memo(({ data, locationColor, title, height = 250 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const options = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'rgba(255,255,255,0.95)',
          titleColor: '#1a1a1a',
          bodyColor: '#333',
          borderColor: alpha(locationColor, 0.2),
          borderWidth: 1,
          cornerRadius: 12,
          padding: 12,
          titleFont: { weight: '700', family: '"Inter", sans-serif' },
          bodyFont: { weight: '600', family: '"Inter", sans-serif' },
          callbacks: { label: (ctx) => '₦' + ctx.parsed.x.toLocaleString() },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: alpha('#000', 0.06), drawBorder: false },
          ticks: {
            font: { size: isMobile ? 8 : 10, weight: '600', family: '"Inter", sans-serif' },
            color: '#666',
            callback: (value) => '₦' + value.toLocaleString(),
          },
        },
        y: {
          grid: { display: false },
          ticks: {
            font: { size: isMobile ? 9 : 11, weight: '700', family: '"Inter", sans-serif' },
            color: '#444',
          },
        },
      },
    }),
    [isMobile]
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${alpha(locationColor, 0.08)}`,
        height: '100%',
        minHeight: 280,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: `0 8px 32px ${alpha(locationColor, 0.1)}`,
          borderColor: alpha(locationColor, 0.2),
        },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${locationColor} 0%, ${alpha(locationColor, 0.3)} 100%)`,
        }}
      />
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mb: 2.5,
            color: locationColor,
            fontSize: { xs: '0.9rem', sm: '1.1rem' },
            fontFamily: '"Inter", sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 20,
              bgcolor: locationColor,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          {title}
        </Typography>
        <Box sx={{ height: isMobile ? 180 : height }}>
          <Bar data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
});

const RadarChartComponent = memo(({ data, locationColor, title, height = 250 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            font: { size: isMobile ? 10 : 12, weight: '700', family: '"Inter", sans-serif' },
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 16,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(255,255,255,0.95)',
          titleColor: '#1a1a1a',
          bodyColor: '#333',
          borderColor: alpha(locationColor, 0.2),
          borderWidth: 1,
          cornerRadius: 12,
          padding: 12,
          titleFont: { weight: '700', family: '"Inter", sans-serif' },
          bodyFont: { weight: '600', family: '"Inter", sans-serif' },
        },
      },
      scales: {
        r: {
          min: 0,
          grid: { color: alpha('#000', 0.08) },
          ticks: {
            font: { size: 9, weight: '600', family: '"Inter", sans-serif' },
            stepSize: Math.ceil(Math.max(...(data.datasets[0]?.data || [0])) / 4),
          },
          pointLabels: {
            font: { size: isMobile ? 8 : 10, weight: '700', family: '"Inter", sans-serif' },
          },
        },
      },
    }),
    [isMobile, data]
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${alpha(locationColor, 0.08)}`,
        height: '100%',
        minHeight: 280,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: `0 8px 32px ${alpha(locationColor, 0.1)}`,
          borderColor: alpha(locationColor, 0.2),
        },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${locationColor} 0%, ${alpha(locationColor, 0.3)} 100%)`,
        }}
      />
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mb: 2.5,
            color: locationColor,
            fontSize: { xs: '0.9rem', sm: '1.1rem' },
            fontFamily: '"Inter", sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 20,
              bgcolor: locationColor,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          {title}
        </Typography>
        <Box sx={{ height: isMobile ? 180 : height }}>
          <Radar data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
});

const StackedBarChart = memo(({ data, locationColor, title, height = 250 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            font: { size: isMobile ? 10 : 12, weight: '700', family: '"Inter", sans-serif' },
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 16,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(255,255,255,0.95)',
          titleColor: '#1a1a1a',
          bodyColor: '#333',
          borderColor: alpha(locationColor, 0.2),
          borderWidth: 1,
          cornerRadius: 12,
          padding: 12,
          titleFont: { weight: '700', family: '"Inter", sans-serif' },
          bodyFont: { weight: '600', family: '"Inter", sans-serif' },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: {
            font: { size: isMobile ? 8 : 10, weight: '600', family: '"Inter", sans-serif' },
            color: '#666',
            maxRotation: isMobile ? 45 : 0,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: alpha('#000', 0.06), drawBorder: false },
          ticks: {
            font: { size: isMobile ? 9 : 11, weight: '600', family: '"Inter", sans-serif' },
            color: '#666',
            callback: (value) => '₦' + value.toLocaleString(),
          },
        },
      },
    }),
    [isMobile]
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${alpha(locationColor, 0.08)}`,
        height: '100%',
        minHeight: 280,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: `0 8px 32px ${alpha(locationColor, 0.1)}`,
          borderColor: alpha(locationColor, 0.2),
        },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${locationColor} 0%, ${alpha(locationColor, 0.3)} 100%)`,
        }}
      />
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mb: 2.5,
            color: locationColor,
            fontSize: { xs: '0.9rem', sm: '1.1rem' },
            fontFamily: '"Inter", sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 20,
              bgcolor: locationColor,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          {title}
        </Typography>
        <Box sx={{ height: isMobile ? 180 : height }}>
          <Bar data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
});

// ============================================
// DATE RANGE PICKER
// ============================================

const CustomDateRangePicker = memo(({ dateRange, onDateRangeChange, locationColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(dateRange.startDate || null);
  const [tempEndDate, setTempEndDate] = useState(dateRange.endDate || null);

  useEffect(() => {
    setTempStartDate(dateRange.startDate || null);
    setTempEndDate(dateRange.endDate || null);
  }, [dateRange]);

  const handleApply = () => {
    onDateRangeChange({ startDate: tempStartDate, endDate: tempEndDate });
    setIsOpen(false);
  };

  const handleReset = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    onDateRangeChange({ startDate: null, endDate: null });
    setIsOpen(false);
  };

  const handleQuickRange = (days) => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    if (days === 0) {
      onDateRangeChange({ startDate: start, endDate: end });
      setIsOpen(false);
      return;
    }
    start.setDate(end.getDate() - days);
    start.setHours(0, 0, 0, 0);
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const handleQuickMonth = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const hasDates = dateRange.startDate || dateRange.endDate;
  const isToday =
    dateRange.startDate &&
    dateRange.endDate &&
    new Date(dateRange.startDate).toDateString() === new Date().toDateString() &&
    new Date(dateRange.endDate).toDateString() === new Date().toDateString();

  const quickRanges = [
    { label: 'Today', days: 0, icon: <TodayIcon sx={{ fontSize: 16 }} /> },
    { label: 'Yesterday', days: 1 },
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'This Month', days: 'month' },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant={hasDates ? 'contained' : 'outlined'}
          size="medium"
          onClick={() => {
            setTempStartDate(dateRange.startDate || null);
            setTempEndDate(dateRange.endDate || null);
            setIsOpen(true);
          }}
          startIcon={<DateRangeIcon />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            fontFamily: '"Inter", sans-serif',
            ...(hasDates && {
              bgcolor: locationColor,
              '&:hover': { bgcolor: locationColor, opacity: 0.9 },
            }),
            ...(!hasDates && {
              borderColor: alpha(locationColor, 0.3),
              color: locationColor,
            }),
          }}
        >
          {hasDates
            ? isToday
              ? 'Today'
              : `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
            : 'Select Date Range'}
        </Button>

        {hasDates && (
          <IconButton
            size="small"
            onClick={() => onDateRangeChange({ startDate: null, endDate: null })}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', p: 2, maxWidth: '480px' },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" fontWeight={700} fontFamily='"Inter", sans-serif'>
            Select Date Range
          </Typography>
          <IconButton onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 1.5, color: 'text.secondary', fontFamily: '"Inter", sans-serif' }}
            >
              Quick Select
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickRanges.map((range, index) => (
                <Button
                  key={index}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    if (range.days === 'month') {
                      handleQuickMonth();
                    } else {
                      handleQuickRange(range.days);
                    }
                  }}
                  startIcon={range.icon}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    fontFamily: '"Inter", sans-serif',
                    borderColor: alpha(locationColor, 0.2),
                    '&:hover': { borderColor: locationColor },
                  }}
                >
                  {range.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mb: 2, color: 'text.secondary', fontFamily: '"Inter", sans-serif' }}
          >
            Custom Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={tempStartDate ? formatDateInput(tempStartDate) : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const date = new Date(value + 'T00:00:00');
                  setTempStartDate(date);
                } else {
                  setTempStartDate(null);
                }
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                '& .MuiInputLabel-root': {
                  fontWeight: 600,
                  fontFamily: '"Inter", sans-serif',
                },
                '& .MuiInputBase-input': {
                  fontWeight: 600,
                  fontFamily: '"Inter", sans-serif',
                },
              }}
            />
            <TextField
              label="End Date"
              type="date"
              value={tempEndDate ? formatDateInput(tempEndDate) : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value) {
                  const date = new Date(value + 'T00:00:00');
                  setTempEndDate(date);
                } else {
                  setTempEndDate(null);
                }
              }}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                '& .MuiInputLabel-root': {
                  fontWeight: 600,
                  fontFamily: '"Inter", sans-serif',
                },
                '& .MuiInputBase-input': {
                  fontWeight: 600,
                  fontFamily: '"Inter", sans-serif',
                },
              }}
            />
          </Box>

          {tempStartDate && tempEndDate && tempStartDate > tempEndDate && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>
              Start date must be before end date
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={handleReset}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: '"Inter", sans-serif',
              borderRadius: '10px',
            }}
          >
            Clear
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={tempStartDate && tempEndDate && tempStartDate > tempEndDate}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: '"Inter", sans-serif',
              borderRadius: '10px',
              bgcolor: locationColor,
              '&:hover': { bgcolor: locationColor, opacity: 0.9 },
              '&:disabled': { bgcolor: 'grey.300' },
            }}
          >
            Apply Range
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

// ============================================
// STATS CARDS
// ============================================

const StatsCards = memo(({ stats, locationColor, isMobile }) => {
  const statItems = [
    {
      label: 'Total Orders',
      value: stats.totalOrders || 0,
      icon: <ReceiptIcon />,
      color: locationColor,
      change: stats.orderChange,
    },
    {
      label: 'Total Revenue',
      value: formatCurrency(stats.totalRevenue || 0),
      icon: <AttachMoneyIcon />,
      color: '#2E7D32',
      change: stats.revenueChange,
    },
    {
      label: 'Completed',
      value: stats.completed || 0,
      icon: <CheckCircleIcon />,
      color: '#2E7D32',
    },
    {
      label: 'Pending',
      value: stats.pending || 0,
      icon: <PendingIcon />,
      color: '#FF9800',
    },
    {
      label: 'Cancelled',
      value: stats.cancelled || 0,
      icon: <CancelIcon />,
      color: '#D32F2F',
    },
  ];

  const displayItems = isMobile ? statItems.slice(0, 4) : statItems;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {displayItems.map((stat, index) => (
        <Grid item xs={6} sm={6} md={isMobile ? 6 : 2.4} key={index}>
          <Card
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: `1px solid ${alpha(stat.color, 0.12)}`,
              background: `linear-gradient(135deg, ${alpha(stat.color, 0.04)} 0%, ${alpha(stat.color, 0.1)} 100%)`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: `0 12px 40px ${alpha(stat.color, 0.15)}`,
                borderColor: stat.color,
              },
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -40,
                right: -40,
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(stat.color, 0.06)} 0%, transparent 70%)`,
                pointerEvents: 'none',
              }}
            />

            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: alpha(stat.color, 0.12),
                    color: stat.color,
                    width: { xs: 44, sm: 56 },
                    height: { xs: 44, sm: 56 },
                    border: `2px solid ${alpha(stat.color, 0.15)}`,
                    boxShadow: `0 4px 16px ${alpha(stat.color, 0.15)}`,
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                      fontSize: { xs: '1.1rem', sm: '1.5rem' },
                      color: stat.color,
                      lineHeight: 1.1,
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      fontFamily: '"Inter", sans-serif',
                      letterSpacing: '0.3px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {stat.label}
                  </Typography>
                  {stat.change !== undefined && stat.change !== null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      {stat.change >= 0 ? (
                        <TrendingUpIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 14, color: '#D32F2F' }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          color: stat.change >= 0 ? '#2E7D32' : '#D32F2F',
                          fontSize: '0.7rem',
                          fontFamily: '"Inter", sans-serif',
                        }}
                      >
                        {stat.change >= 0 ? '+' : ''}
                        {stat.change.toFixed(1)}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
});

// ============================================
// FILTER BAR
// ============================================

const FilterBar = memo(
  ({
    filters,
    onFilterChange,
    onSearch,
    onRefresh,
    isFetching,
    locationColor,
    locationId,
    onLocationChange,
  }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [tempFilters, setTempFilters] = useState(filters);
    const [searchValue, setSearchValue] = useState(filters.search || '');
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
      setSearchValue(filters.search || '');
    }, [filters.search]);

    const handleFilterClick = (event) => {
      setAnchorEl(event.currentTarget);
      setTempFilters(filters);
    };

    const handleFilterClose = () => setAnchorEl(null);

    const handleApplyFilters = () => {
      onFilterChange(tempFilters);
      handleFilterClose();
    };

    const handleResetFilters = () => {
      const resetFilters = {
        status: 'ALL',
        paymentStatus: 'ALL',
        orderType: 'ALL',
        search: searchValue,
      };
      setTempFilters(resetFilters);
      onFilterChange(resetFilters);
      handleFilterClose();
    };

    const handleTempChange = (field, value) => {
      setTempFilters((prev) => ({ ...prev, [field]: value }));
    };

    const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchValue(value);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        onSearch(value);
      }, 400);
    };

    const filterCount = Object.values(filters).filter(
      (v) => v !== 'ALL' && v !== '' && v !== null && v !== undefined
    ).length;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search by order number, customer, or table..."
            value={searchValue}
            onChange={handleSearchChange}
            sx={{
              flex: 1,
              minWidth: '180px',
              maxWidth: { xs: '100%', sm: '350px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                bgcolor: 'white',
                fontWeight: 600,
                '& fieldset': { borderColor: alpha(locationColor, 0.2) },
                '&:hover fieldset': { borderColor: locationColor },
                '&.Mui-focused fieldset': { borderColor: locationColor },
              },
              '& .MuiInputBase-input': {
                fontWeight: 600,
                fontFamily: '"Inter", sans-serif',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
              endAdornment: searchValue && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchValue('');
                      onSearch('');
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: '140px' }}>
            <InputLabel
              sx={{ fontWeight: 700, fontFamily: '"Inter", sans-serif' }}
            >
              Location
            </InputLabel>
            <Select
              value={locationId || ''}
              onChange={(e) => onLocationChange(e.target.value)}
              label="Location"
              sx={{
                borderRadius: '12px',
                bgcolor: 'white',
                '& .MuiSelect-select': {
                  fontWeight: 600,
                  fontFamily: '"Inter", sans-serif',
                },
              }}
            >
              {LOCATIONS.map((loc) => (
                <MenuItem
                  key={loc.id}
                  value={loc.id}
                  sx={{ fontWeight: 600, fontFamily: '"Inter", sans-serif' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{loc.icon}</span>
                    <span>{loc.name}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="medium"
            onClick={handleFilterClick}
            startIcon={<FilterAltIcon />}
            endIcon={
              filterCount > 0 ? (
                <Badge badgeContent={filterCount} color="primary" />
              ) : null
            }
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: '"Inter", sans-serif',
              borderColor: alpha(locationColor, 0.3),
              color: locationColor,
              '&:hover': {
                borderColor: locationColor,
                bgcolor: alpha(locationColor, 0.05),
              },
            }}
          >
            Filters
          </Button>

          <Tooltip title="Refresh Data">
            <IconButton
              onClick={onRefresh}
              disabled={isFetching}
              sx={{
                bgcolor: alpha(locationColor, 0.08),
                '&:hover': { bgcolor: alpha(locationColor, 0.15) },
                borderRadius: '10px',
                width: 40,
                height: 40,
              }}
            >
              {isFetching ? (
                <CircularProgress size={22} />
              ) : (
                <RefreshIcon sx={{ color: locationColor }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Export Data">
            <IconButton
              sx={{
                bgcolor: alpha(locationColor, 0.08),
                '&:hover': { bgcolor: alpha(locationColor, 0.15) },
                borderRadius: '10px',
                width: 40,
                height: 40,
              }}
            >
              <GetAppIcon sx={{ color: locationColor }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              borderRadius: '16px',
              p: 2.5,
              minWidth: { xs: 'calc(100vw - 32px)', sm: '360px' },
              maxWidth: '400px',
              boxShadow: (theme) => theme.shadows[12],
            },
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 2, fontFamily: '"Inter", sans-serif' }}
          >
            Filter Orders
          </Typography>

          <Stack spacing={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel
                sx={{ fontWeight: 600, fontFamily: '"Inter", sans-serif' }}
              >
                Order Status
              </InputLabel>
              <Select
                value={tempFilters.status || 'ALL'}
                onChange={(e) => handleTempChange('status', e.target.value)}
                label="Order Status"
                sx={{
                  borderRadius: '10px',
                  '& .MuiSelect-select': {
                    fontWeight: 600,
                    fontFamily: '"Inter", sans-serif',
                  },
                }}
              >
                <MenuItem value="ALL" sx={{ fontWeight: 600 }}>
                  All Status
                </MenuItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key} sx={{ fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: config.color,
                        }}
                      />
                      <span>{config.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel
                sx={{ fontWeight: 600, fontFamily: '"Inter", sans-serif' }}
              >
                Payment Status
              </InputLabel>
              <Select
                value={tempFilters.paymentStatus || 'ALL'}
                onChange={(e) => handleTempChange('paymentStatus', e.target.value)}
                label="Payment Status"
                sx={{
                  borderRadius: '10px',
                  '& .MuiSelect-select': {
                    fontWeight: 600,
                    fontFamily: '"Inter", sans-serif',
                  },
                }}
              >
                <MenuItem value="ALL" sx={{ fontWeight: 600 }}>
                  All Payment Status
                </MenuItem>
                {Object.entries(PAYMENT_STATUS_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key} sx={{ fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: config.color,
                        }}
                      />
                      <span>{config.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel
                sx={{ fontWeight: 600, fontFamily: '"Inter", sans-serif' }}
              >
                Order Type
              </InputLabel>
              <Select
                value={tempFilters.orderType || 'ALL'}
                onChange={(e) => handleTempChange('orderType', e.target.value)}
                label="Order Type"
                sx={{
                  borderRadius: '10px',
                  '& .MuiSelect-select': {
                    fontWeight: 600,
                    fontFamily: '"Inter", sans-serif',
                  },
                }}
              >
                <MenuItem value="ALL" sx={{ fontWeight: 600 }}>
                  All Types
                </MenuItem>
                {Object.entries(ORDER_TYPE_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key} sx={{ fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {config.icon}
                      <span>{config.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResetFilters}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                Reset
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyFilters}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontFamily: '"Inter", sans-serif',
                  bgcolor: locationColor,
                  '&:hover': { bgcolor: locationColor, opacity: 0.9 },
                }}
              >
                Apply
              </Button>
            </Box>
          </Stack>
        </Popover>
      </Box>
    );
  }
);

// ============================================
// ORDER TABLE ROW
// ============================================

const OrderTableRow = memo(({ order, locationColor, onViewDetails, index }) => {
  let items = [];
  try {
    items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items || [];
  } catch {
    items = [];
  }

  const itemCount = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const statusColor = getStatusColor(order.order_status);
  const statusLabel = getStatusLabel(order.order_status);
  const paymentStatusColor = getPaymentStatusColor(order.payment_status);
  const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);

  return (
    <TableRow
      hover
      sx={{
        cursor: 'pointer',
        '&:hover': {
          bgcolor: alpha(locationColor, 0.04),
          '& .MuiTableCell-root': { color: 'text.primary' },
        },
        transition: 'background-color 0.2s ease',
        bgcolor: index % 2 === 0 ? 'transparent' : alpha('#000', 0.01),
      }}
      onClick={() => onViewDetails(order)}
    >
      <TableCell>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            fontFamily: '"Inter", sans-serif',
            color: locationColor,
          }}
        >
          {order.order_number}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.55rem', sm: '0.75rem' },
            fontWeight: 600,
            fontFamily: '"Inter", sans-serif',
          }}
        >
          {formatTime(order.order_time)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            fontFamily: '"Inter", sans-serif',
          }}
        >
          {getOrderTypeLabel(order.order_type)}
        </Typography>
        {order.table_name && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{
              fontSize: { xs: '0.55rem', sm: '0.75rem' },
              fontWeight: 600,
              fontFamily: '"Inter", sans-serif',
            }}
          >
            {order.table_name}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            fontFamily: '"Inter", sans-serif',
          }}
        >
          {order.waiter_name || 'N/A'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            fontFamily: '"Inter", sans-serif',
          }}
        >
          {itemCount} items
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            bgcolor: alpha(statusColor, 0.12),
            color: statusColor,
            fontWeight: 700,
            fontSize: { xs: '0.55rem', sm: '0.65rem' },
            height: { xs: 20, sm: 24 },
            border: `1px solid ${alpha(statusColor, 0.2)}`,
            fontFamily: '"Inter", sans-serif',
          }}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={paymentStatusLabel}
          size="small"
          sx={{
            bgcolor: alpha(paymentStatusColor, 0.12),
            color: paymentStatusColor,
            fontWeight: 700,
            fontSize: { xs: '0.5rem', sm: '0.6rem' },
            height: { xs: 18, sm: 22 },
            border: `1px solid ${alpha(paymentStatusColor, 0.2)}`,
            fontFamily: '"Inter", sans-serif',
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="h6"
          fontWeight={800}
          color={locationColor}
          sx={{
            fontSize: { xs: '0.85rem', sm: '1rem' },
            fontFamily: '"Inter", sans-serif',
          }}
        >
          {formatCurrency(order.total_amount)}
        </Typography>
      </TableCell>
    </TableRow>
  );
});

// ============================================
// ORDER DETAIL DIALOG
// ============================================

const OrderDetailDialog = memo(({ open, order, onClose, locationColor }) => {
  const handlePrintReceipt = useCallback(() => {
    if (!order) return;

    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) {
      alert('Please allow popups to print receipts');
      return;
    }

    let items = [];
    try {
      items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items || [];
    } catch {
      items = [];
    }

    const statusLabel = getStatusLabel(order.order_status);
    const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);
    const orderTypeLabel = getOrderTypeLabel(order.order_type);

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${order.order_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', monospace;
            padding: 20px;
            max-width: 350px;
            margin: 0 auto;
            background: white;
            color: #000;
          }
          .header {
            text-align: center;
            border-bottom: 2px dashed #000;
            padding-bottom: 15px;
            margin-bottom: 15px;
          }
          .header h2 { font-size: 20px; margin-bottom: 5px; }
          .header .order-number { font-size: 16px; font-weight: bold; }
          .header .detail { font-size: 12px; color: #555; margin: 2px 0; }
          .items { margin: 15px 0; }
          .item-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            border-bottom: 1px dotted #ddd;
            font-size: 13px;
          }
          .totals { margin-top: 15px; border-top: 2px dashed #000; padding-top: 10px; }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 3px 0;
            font-size: 13px;
          }
          .grand-total {
            font-size: 18px;
            font-weight: bold;
            border-top: 2px solid #000;
            padding-top: 10px;
            margin-top: 5px;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 2px dashed #000;
            font-size: 11px;
            color: #666;
          }
          .status {
            display: inline-block;
            padding: 2px 10px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: bold;
            margin-top: 5px;
          }
          @media print {
            .no-print { display: none !important; }
            body { padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${order.location_name || 'Restaurant'}</h2>
          <div class="order-number">${order.order_number}</div>
          <div class="detail">${formatDateTime(order.order_time)}</div>
          <div class="detail">${orderTypeLabel}</div>
          ${order.table_name ? `<div class="detail">Table: ${order.table_name}</div>` : ''}
          ${order.waiter_name ? `<div class="detail">Waiter: ${order.waiter_name}</div>` : ''}
          <div>
            <span class="status" style="background:${getStatusColor(order.order_status)}20;color:${getStatusColor(order.order_status)};border:1px solid ${getStatusColor(order.order_status)}">
              ${statusLabel}
            </span>
            <span class="status" style="background:${getPaymentStatusColor(order.payment_status)}20;color:${getPaymentStatusColor(order.payment_status)};border:1px solid ${getPaymentStatusColor(order.payment_status)}">
              ${paymentStatusLabel}
            </span>
          </div>
        </div>

        <div class="items">
          <div style="display:flex;justify-content:space-between;font-weight:bold;padding:5px 0;border-bottom:2px solid #000;font-size:13px;">
            <span>Item</span>
            <span>Qty</span>
            <span>Price</span>
          </div>
          ${items.map(item => `
            <div class="item-row">
              <span>${item.name}</span>
              <span>×${item.quantity || 1}</span>
              <span>${formatCurrency((item.price || 0) * (item.quantity || 1))}</span>
            </div>
          `).join('')}
        </div>

        <div class="totals">
          <div class="total-row">
            <span>Subtotal</span>
            <span>${formatCurrency(order.subtotal)}</span>
          </div>
          ${order.tax > 0 ? `
            <div class="total-row">
              <span>Tax</span>
              <span>${formatCurrency(order.tax)}</span>
            </div>
          ` : ''}
          ${order.discount > 0 ? `
            <div class="total-row">
              <span>Discount</span>
              <span>-${formatCurrency(order.discount)}</span>
            </div>
          ` : ''}
          <div class="total-row grand-total">
            <span><strong>TOTAL</strong></span>
            <span><strong>${formatCurrency(order.total_amount)}</strong></span>
          </div>
        </div>

        <div class="footer">
          <p><strong>Thank you!</strong></p>
          <p>${new Date().toLocaleString()}</p>
        </div>
        
        <button class="no-print" style="display:block;margin:20px auto;padding:10px 30px;cursor:pointer;" onclick="window.print()">🖨️ Print</button>
        <button class="no-print" style="display:block;margin:5px auto;padding:5px 20px;cursor:pointer;" onclick="window.close()">Close</button>
      </body>
      </html>
    `;

    printWindow.document.write(receiptHTML);
    printWindow.document.close();

    printWindow.onload = () => {
      setTimeout(() => printWindow.print(), 500);
    };
  }, [order]);

  if (!order) return null;

  let items = [];
  try {
    items = typeof order.items === 'string' ? JSON.parse(order.items) : order.items || [];
  } catch {
    items = [];
  }

  const statusColor = getStatusColor(order.order_status);
  const statusLabel = getStatusLabel(order.order_status);
  const paymentStatusColor = getPaymentStatusColor(order.payment_status);
  const paymentStatusLabel = getPaymentStatusLabel(order.payment_status);
  const orderTypeLabel = getOrderTypeLabel(order.order_type);

  const totalItems = items.reduce((sum, i) => sum + (i.quantity || 1), 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxHeight: '90vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          borderRadius: '20px 20px 0 0',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ReceiptIcon sx={{ color: locationColor, fontSize: 28 }} />
          <Typography
            variant="h5"
            component="span"
            fontWeight={700}
            sx={{
              fontSize: { xs: '1rem', sm: '1.5rem' },
              fontFamily: '"Inter", sans-serif',
            }}
          >
            {order.order_number}
          </Typography>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              bgcolor: alpha(statusColor, 0.12),
              color: statusColor,
              fontWeight: 700,
              fontFamily: '"Inter", sans-serif',
            }}
          />
          <Chip
            label={paymentStatusLabel}
            size="small"
            sx={{
              bgcolor: alpha(paymentStatusColor, 0.12),
              color: paymentStatusColor,
              fontWeight: 700,
              fontFamily: '"Inter", sans-serif',
            }}
          />
        </Box>
        <IconButton onClick={onClose} sx={{ bgcolor: alpha('#000', 0.04) }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        <Box
          sx={{
            p: 3,
            bgcolor: alpha(locationColor, 0.02),
            borderBottom: `1px solid ${alpha('#000', 0.06)}`,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: 'block', mb: 0.5, fontFamily: '"Inter", sans-serif' }}
            >
              Order Type
            </Typography>
            <Typography
              variant="body1"
              fontWeight={700}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, fontFamily: '"Inter", sans-serif' }}
            >
              {getOrderTypeIcon(order.order_type)}
              {orderTypeLabel}
            </Typography>
          </Box>

          {order.table_name && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ display: 'block', mb: 0.5, fontFamily: '"Inter", sans-serif' }}
              >
                Table
              </Typography>
              <Typography variant="body1" fontWeight={700} fontFamily='"Inter", sans-serif'>
                {order.table_name}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: 'block', mb: 0.5, fontFamily: '"Inter", sans-serif' }}
            >
              Waiter
            </Typography>
            <Typography variant="body1" fontWeight={700} fontFamily='"Inter", sans-serif'>
              {order.waiter_name || 'N/A'}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: 'block', mb: 0.5, fontFamily: '"Inter", sans-serif' }}
            >
              Order Time
            </Typography>
            <Typography variant="body1" fontWeight={700} fontFamily='"Inter", sans-serif'>
              {formatDateTime(order.order_time)}
            </Typography>
          </Box>

          {order.customer_name && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ display: 'block', mb: 0.5, fontFamily: '"Inter", sans-serif' }}
              >
                Customer
              </Typography>
              <Typography variant="body1" fontWeight={700} fontFamily='"Inter", sans-serif'>
                {order.customer_name}
                {order.customer_phone && ` (${order.customer_phone})`}
              </Typography>
            </Box>
          )}

          {order.location_name && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ display: 'block', mb: 0.5, fontFamily: '"Inter", sans-serif' }}
              >
                Location
              </Typography>
              <Typography variant="body1" fontWeight={700} fontFamily='"Inter", sans-serif'>
                {order.location_name}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                fontSize: { xs: '0.95rem', sm: '1.25rem' },
                fontFamily: '"Inter", sans-serif',
              }}
            >
              Order Items ({totalItems})
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: alpha('#000', 0.02),
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 100px',
                gap: 1,
                p: 2,
                bgcolor: alpha('#000', 0.04),
                fontWeight: 700,
                fontSize: '0.8rem',
                color: 'text.secondary',
                borderBottom: `1px solid ${alpha('#000', 0.06)}`,
                fontFamily: '"Inter", sans-serif',
              }}
            >
              <Typography variant="caption" fontWeight={700} fontFamily='"Inter", sans-serif'>
                Item
              </Typography>
              <Typography
                variant="caption"
                fontWeight={700}
                align="center"
                fontFamily='"Inter", sans-serif'
              >
                Qty
              </Typography>
              <Typography
                variant="caption"
                fontWeight={700}
                align="right"
                fontFamily='"Inter", sans-serif'
              >
                Amount
              </Typography>
            </Box>

            {items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 80px 100px',
                  gap: 1,
                  p: 2,
                  borderBottom:
                    idx < items.length - 1 ? `1px solid ${alpha('#000', 0.04)}` : 'none',
                  alignItems: 'center',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: alpha(locationColor, 0.02) },
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    {item.name}
                  </Typography>
                  {item.notes && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        fontStyle: 'italic',
                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                        fontWeight: 600,
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      * {item.notes}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  align="center"
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  ×{item.quantity || 1}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={800}
                  align="right"
                  color={locationColor}
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    fontFamily: '"Inter", sans-serif',
                  }}
                >
                  {formatCurrency((item.price || 0) * (item.quantity || 1))}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: `2px solid ${alpha('#000', 0.06)}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={700}
                fontFamily='"Inter", sans-serif'
              >
                Subtotal
              </Typography>
              <Typography variant="body2" fontWeight={700} fontFamily='"Inter", sans-serif'>
                {formatCurrency(order.subtotal)}
              </Typography>
            </Box>
            {order.tax > 0 && (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={700}
                  fontFamily='"Inter", sans-serif'
                >
                  Tax
                </Typography>
                <Typography variant="body2" fontWeight={700} fontFamily='"Inter", sans-serif'>
                  {formatCurrency(order.tax)}
                </Typography>
              </Box>
            )}
            {order.discount > 0 && (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight={700}
                  fontFamily='"Inter", sans-serif'
                >
                  Discount
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="#D32F2F"
                  fontFamily='"Inter", sans-serif'
                >
                  -{formatCurrency(order.discount)}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 3, pt: 1, borderTop: `1px solid ${alpha('#000', 0.06)}` }}>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                Total
              </Typography>
              <Typography
                variant="h6"
                fontWeight={800}
                color={locationColor}
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                {formatCurrency(order.total_amount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontFamily: '"Inter", sans-serif',
            borderRadius: '10px',
            px: 4,
          }}
        >
          Close
        </Button>
        {order.order_status !== 'CANCELLED' && (
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrintReceipt}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: '"Inter", sans-serif',
              borderRadius: '10px',
              bgcolor: locationColor,
              '&:hover': { bgcolor: locationColor, opacity: 0.9 },
            }}
          >
            Print Receipt
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

// Helper function for order type icon
const getOrderTypeIcon = (type) => {
  return ORDER_TYPE_CONFIG[type]?.icon || <TableRestaurantIcon />;
};

// ============================================
// MAIN COMPONENT - SalesSummary
// ============================================

const SalesSummary = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Location config
  const locationConfig = useMemo(() => {
    const config = LOCATIONS.find((l) => l.name.toLowerCase() === locationKey.toLowerCase());
    return config || LOCATIONS[0];
  }, [locationKey]);

  const LOCATION_COLOR = locationConfig.color;
  const LOCATION_NAME = locationConfig.name;
  const LOCATION_ID = locationConfig.id;

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState(LOCATION_ID);
  const [activeTab, setActiveTab] = useState(0);

  const getTodayRange = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);
    return { startDate: today, endDate: todayEnd };
  }, []);

  const [filters, setFilters] = useState({
    search: '',
    status: 'ALL',
    paymentStatus: 'ALL',
    orderType: 'ALL',
  });

  const [dateRange, setDateRange] = useState(getTodayRange());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const isMountedRef = useRef(true);
  const fetchTimeoutRef = useRef(null);
  const isInitialMountRef = useRef(true);

  const [chartData, setChartData] = useState({
    daily: { labels: [], datasets: [] },
    status: { labels: [], datasets: [] },
    payment: { labels: [], datasets: [] },
    revenue: { labels: [], datasets: [] },
    paymentMethods: { labels: [], datasets: [] },
    hourly: { labels: [], datasets: [] },
  });

  // ============================================
  // CHART DATA GENERATION
  // ============================================

  const generateChartData = useCallback(
    (ordersList) => {
      const dailyMap = {};
      const statusCounts = { PENDING: 0, IN_PROGRESS: 0, READY: 0, COMPLETED: 0, CANCELLED: 0 };
      const paymentCounts = { UNPAID: 0, PAID: 0, PARTIAL: 0, REFUNDED: 0 };
      const paymentMethodCounts = {};
      const hourlyData = {};

      ordersList.forEach((order) => {
        const date = formatDate(order.order_date);
        if (!dailyMap[date]) {
          dailyMap[date] = { revenue: 0, orders: 0 };
        }
        dailyMap[date].revenue += order.total_amount || 0;
        dailyMap[date].orders += 1;

        if (order.order_status) {
          statusCounts[order.order_status] = (statusCounts[order.order_status] || 0) + 1;
        }
        if (order.payment_status) {
          paymentCounts[order.payment_status] = (paymentCounts[order.payment_status] || 0) + 1;
        }

        if (order.payments && Array.isArray(order.payments)) {
          order.payments.forEach((payment) => {
            const method = payment.payment_method || 'Unknown';
            paymentMethodCounts[method] = (paymentMethodCounts[method] || 0) + (payment.amount || 0);
          });
        }

        if (order.order_time) {
          const hour = new Date(order.order_time).getHours();
          const hourKey = `${hour}:00`;
          hourlyData[hourKey] = (hourlyData[hourKey] || 0) + 1;
        }
      });

      const dates = Object.keys(dailyMap).sort();
      const revenues = dates.map((d) => dailyMap[d].revenue);
      const orderCounts = dates.map((d) => dailyMap[d].orders);

      const sortedHours = Object.keys(hourlyData).sort((a, b) => parseInt(a) - parseInt(b));
      const hourlyCounts = sortedHours.map((h) => hourlyData[h]);

      const statusColors = {
        PENDING: '#FF9800',
        IN_PROGRESS: '#2196F3',
        READY: '#4CAF50',
        COMPLETED: '#2E7D32',
        CANCELLED: '#D32F2F',
      };

      const paymentColors = {
        UNPAID: '#F44336',
        PAID: '#4CAF50',
        PARTIAL: '#FF9800',
        REFUNDED: '#9E9E9E',
      };

      const paymentMethodColors = [
        '#4CAF50',
        '#1976D2',
        '#9C27B0',
        '#FF6F00',
        '#FF9800',
        '#00BCD4',
        '#795548',
      ];

      setChartData({
        daily: {
          labels: dates,
          datasets: [
            {
              label: 'Revenue (₦)',
              data: revenues,
              backgroundColor: alpha(LOCATION_COLOR, 0.5),
              borderColor: LOCATION_COLOR,
              borderWidth: 2,
              borderRadius: 6,
            },
            {
              label: 'Orders',
              data: orderCounts,
              backgroundColor: alpha('#FF9800', 0.5),
              borderColor: '#FF9800',
              borderWidth: 2,
              borderRadius: 6,
            },
          ],
        },
        status: {
          labels: Object.keys(statusCounts).map((s) => getStatusLabel(s)),
          datasets: [
            {
              data: Object.values(statusCounts),
              backgroundColor: Object.keys(statusCounts).map((s) =>
                alpha(statusColors[s] || '#9E9E9E', 0.7)
              ),
              borderColor: Object.keys(statusCounts).map((s) => statusColors[s] || '#9E9E9E'),
              borderWidth: 2,
              hoverOffset: 10,
            },
          ],
        },
        payment: {
          labels: Object.keys(paymentCounts).map((p) => getPaymentStatusLabel(p)),
          datasets: [
            {
              data: Object.values(paymentCounts),
              backgroundColor: Object.keys(paymentCounts).map((p) =>
                alpha(paymentColors[p] || '#9E9E9E', 0.7)
              ),
              borderColor: Object.keys(paymentCounts).map((p) => paymentColors[p] || '#9E9E9E'),
              borderWidth: 2,
              hoverOffset: 10,
            },
          ],
        },
        revenue: {
          labels: dates.slice(-7),
          datasets: [
            {
              label: 'Revenue Trend',
              data: revenues.slice(-7),
              backgroundColor: alpha(LOCATION_COLOR, 0.1),
              borderColor: LOCATION_COLOR,
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointBackgroundColor: LOCATION_COLOR,
              pointBorderColor: 'white',
              pointBorderWidth: 2,
              pointRadius: 5,
              pointHoverRadius: 8,
            },
          ],
        },
        paymentMethods: {
          labels: Object.keys(paymentMethodCounts),
          datasets: [
            {
              data: Object.values(paymentMethodCounts),
              backgroundColor: Object.keys(paymentMethodCounts).map(
                (_, i) => paymentMethodColors[i % paymentMethodColors.length]
              ),
              borderColor: 'white',
              borderWidth: 2,
              hoverOffset: 10,
            },
          ],
        },
        hourly: {
          labels: sortedHours,
          datasets: [
            {
              label: 'Orders by Hour',
              data: hourlyCounts,
              backgroundColor: alpha(LOCATION_COLOR, 0.5),
              borderColor: LOCATION_COLOR,
              borderWidth: 2,
              borderRadius: 6,
              hoverBackgroundColor: alpha(LOCATION_COLOR, 0.8),
            },
          ],
        },
      });
    },
    [LOCATION_COLOR]
  );

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchOrders = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      console.debug(`[SalesSummary] Fetching sales data for ${LOCATION_NAME}...`);
      setIsFetching(true);

      const params = {
        location_id: selectedLocationId,
        limit: 500,
        skip: 0,
      };

      if (filters.status !== 'ALL') params.order_status = filters.status;
      if (filters.paymentStatus !== 'ALL') params.payment_status = filters.paymentStatus;
      if (filters.orderType !== 'ALL') params.order_type = filters.orderType;

      if (dateRange.startDate) {
        const dateStr = formatDateForAPI(dateRange.startDate);
        if (dateStr) params.date_from = dateStr;
      }
      if (dateRange.endDate) {
        const dateStr = formatDateForAPI(dateRange.endDate);
        if (dateStr) params.date_to = dateStr;
      }

      if (filters.search && filters.search.trim()) {
        params.search = filters.search.trim();
      }

      console.debug('[SalesSummary] Fetching with params:', params);

      const data = await apiRequest('/orders/history/', { params });

      if (isMountedRef.current) {
        let ordersList = [];
        if (data && typeof data === 'object') {
          if (Array.isArray(data.items)) {
            ordersList = data.items;
          } else if (Array.isArray(data)) {
            ordersList = data;
          }
        }

        console.debug(`[SalesSummary] Parsed ${ordersList.length} orders`);

        ordersList = ordersList.map((order) => ({
          ...order,
          location_name: LOCATION_NAME,
        }));

        setOrders(ordersList);
        setFilteredOrders(ordersList);

        const totalOrdersCount = ordersList.length;
        const totalRevenue = ordersList
          .filter((o) => o.order_status === 'COMPLETED')
          .reduce((sum, o) => sum + (o.total_amount || 0), 0);
        const completed = ordersList.filter((o) => o.order_status === 'COMPLETED').length;
        const pending = ordersList.filter((o) => o.order_status === 'PENDING').length;
        const cancelled = ordersList.filter((o) => o.order_status === 'CANCELLED').length;
        const averageOrderValue = completed > 0 ? totalRevenue / completed : 0;

        setStats({
          totalOrders: totalOrdersCount,
          totalRevenue,
          averageOrderValue,
          completed,
          pending,
          cancelled,
        });

        generateChartData(ordersList);
      }
    } catch (err) {
      console.error('[SalesSummary] Error fetching sales data:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch sales data: ${err.message}`,
          severity: 'error',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [selectedLocationId, LOCATION_NAME, filters, dateRange, generateChartData]);

  // ============================================
  // INITIALIZATION
  // ============================================

  useEffect(() => {
    isMountedRef.current = true;

    const loadData = async () => {
      await fetchOrders();
      if (isMountedRef.current) {
        setIsLoading(false);
        isInitialMountRef.current = false;
      }
    };

    loadData();

    return () => {
      isMountedRef.current = false;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [fetchOrders]);

  // ============================================
  // RE-FETCH ON DEPENDENCY CHANGE
  // ============================================

  useEffect(() => {
    if (isInitialMountRef.current) return;

    if (isMountedRef.current) {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      fetchTimeoutRef.current = setTimeout(() => {
        fetchOrders();
      }, 300);
    }
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [filters, dateRange, selectedLocationId, fetchOrders]);

  // ============================================
  // TOKEN AUTO-REFRESH
  // ============================================

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (token && isTokenExpired(token)) {
        await refreshAuthToken();
      }
    };
    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  // ============================================
  // HANDLERS
  // ============================================

  const handleFilterChange = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleSearch = useCallback((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(0);
  }, []);

  const handleDateRangeChange = useCallback((newDateRange) => {
    setDateRange(newDateRange);
    setPage(0);
  }, []);

  const handleLocationChange = useCallback((locationId) => {
    setSelectedLocationId(locationId);
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchOrders();
    setSnackbar({ open: true, message: 'Data refreshed successfully', severity: 'success' });
  }, [fetchOrders]);

  const handleViewDetails = useCallback((order) => {
    setSelectedOrder(order);
    setDetailDialogOpen(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedOrder(null);
  }, []);

  const handleTabChange = (_, newValue) => setActiveTab(newValue);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ============================================
  // RENDER HELPERS
  // ============================================

  const paginatedOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const currentLocation = useMemo(() => {
    return LOCATIONS.find((l) => l.id === selectedLocationId) || locationConfig;
  }, [selectedLocationId, locationConfig]);

  const currentColor = currentLocation.color;
  const currentName = currentLocation.name;

  // Dynamic Sidebar import
  let Sidebar;
  try {
    Sidebar = require(`../../../components/RestaurantPOS/manager/${locationKey}/Sidebar`).default;
  } catch {
    Sidebar = require('../../../components/RestaurantPOS/manager/african/Sidebar').default;
  }

  // ============================================
  // LOADING STATE
  // ============================================

  if (isLoading) {
    return <Preloader text={`Loading ${currentName} Sales Summary...`} size={70} fullScreen />;
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <>
      <div className="epos-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div className="epos-main-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Sidebar activeMenu="Sales Summary" setActiveMenu={() => {}} />

          <div
            className="epos-main"
            style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}
          >
            <Header />

            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: { xs: 1.5, sm: 2, md: 3 },
                bgcolor: '#f5f7fa',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
              }}
            >
              {/* Header Section */}
              <Box
                sx={{
                  bgcolor: 'white',
                  borderRadius: '16px',
                  p: { xs: 1.5, sm: 2.5, md: 3 },
                  mb: { xs: 2, sm: 3 },
                  border: `1px solid ${alpha(currentColor, 0.15)}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: currentColor,
                        fontWeight: 800,
                        fontSize: { xs: '1.2rem', sm: '1.8rem', md: '2rem' },
                        fontFamily: '"Inter", sans-serif',
                        letterSpacing: '-0.5px',
                      }}
                    >
                      📊 Sales Dashboard
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        fontWeight: 700,
                        fontSize: { xs: '0.8rem', sm: '1rem' },
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      {filteredOrders.length} orders · {currentName}
                      {dateRange.startDate && dateRange.endDate && (
                        <span style={{ marginLeft: '8px', color: currentColor, fontWeight: 700 }}>
                          ·{' '}
                          {new Date(dateRange.startDate).toDateString() ===
                          new Date(dateRange.endDate).toDateString()
                            ? formatDate(dateRange.startDate)
                            : `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`}
                        </span>
                      )}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<span>{currentLocation.icon}</span>}
                      label={currentName}
                      sx={{
                        bgcolor: alpha(currentColor, 0.1),
                        color: currentColor,
                        fontWeight: 800,
                        fontSize: { xs: '0.7rem', sm: '0.9rem' },
                        px: 1,
                        height: { xs: 30, sm: 36 },
                        fontFamily: '"Inter", sans-serif',
                        '& .MuiChip-label': { fontWeight: 800, px: { xs: 0.5, sm: 1 } },
                      }}
                    />
                    <Chip
                      icon={<AssessmentIcon />}
                      label="Manager View"
                      variant="outlined"
                      sx={{
                        borderColor: alpha(currentColor, 0.3),
                        fontWeight: 700,
                        fontSize: { xs: '0.6rem', sm: '0.8rem' },
                        height: { xs: 26, sm: 32 },
                        fontFamily: '"Inter", sans-serif',
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Stats Cards */}
              <StatsCards stats={stats} locationColor={currentColor} isMobile={isMobile} />

              {/* Date Range Picker */}
              <Box sx={{ mb: 3 }}>
                <CustomDateRangePicker
                  dateRange={dateRange}
                  onDateRangeChange={handleDateRangeChange}
                  locationColor={currentColor}
                />
              </Box>

              {/* Filter Bar */}
              <FilterBar
                filters={filters}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onRefresh={handleRefresh}
                isFetching={isFetching}
                locationColor={currentColor}
                locationId={selectedLocationId}
                onLocationChange={handleLocationChange}
              />

              {/* Tabs */}
              <Box sx={{ mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    bgcolor: 'white',
                    borderRadius: '14px',
                    p: 0.5,
                    border: `1px solid ${alpha(currentColor, 0.08)}`,
                    '& .MuiTabs-indicator': {
                      height: 3,
                      bgcolor: currentColor,
                      borderRadius: '3px',
                    },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 700,
                      fontFamily: '"Inter", sans-serif',
                      fontSize: { xs: '0.65rem', sm: '0.875rem' },
                      minHeight: { xs: 36, sm: 48 },
                      px: { xs: 1.5, sm: 3 },
                      borderRadius: '10px',
                      '&.Mui-selected': {
                        color: currentColor,
                        bgcolor: alpha(currentColor, 0.06),
                      },
                    },
                  }}
                >
                  <Tab icon={<BarChartIcon />} label="Overview" iconPosition="start" />
                  <Tab icon={<ShowChartIcon />} label="Revenue Trends" iconPosition="start" />
                  <Tab icon={<PieChartIcon />} label="Payment Methods" iconPosition="start" />
                  <Tab icon={<TimelineIcon />} label="Daily Activity" iconPosition="start" />
                </Tabs>
              </Box>

              {/* Tab Panels */}
              {activeTab === 0 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12}>
                    <StackedBarChart
                      data={chartData.daily}
                      locationColor={currentColor}
                      title="Daily Sales Overview"
                      height={300}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PolarChart
                      data={chartData.status}
                      locationColor={currentColor}
                      title="Order Status Distribution"
                      height={280}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <PolarChart
                      data={chartData.payment}
                      locationColor={currentColor}
                      title="Payment Status Distribution"
                      height={280}
                    />
                  </Grid>
                </Grid>
              )}

              {activeTab === 1 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12}>
                    <GradientAreaChart
                      data={chartData.revenue}
                      locationColor={currentColor}
                      title="Revenue Trend (Last 7 Days)"
                      height={300}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: '16px',
                        border: `1px solid ${alpha(currentColor, 0.1)}`,
                        p: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 32px ${alpha(currentColor, 0.08)}`,
                        },
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{ mb: 2, color: currentColor, fontFamily: '"Inter", sans-serif' }}
                      >
                        Key Metrics
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={700}
                            fontFamily='"Inter", sans-serif'
                          >
                            Total Revenue
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color={currentColor}
                            fontFamily='"Inter", sans-serif'
                          >
                            {formatCurrency(stats.totalRevenue || 0)}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={700}
                            fontFamily='"Inter", sans-serif'
                          >
                            Average Order Value
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color={currentColor}
                            fontFamily='"Inter", sans-serif'
                          >
                            {formatCurrency(stats.averageOrderValue || 0)}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={700}
                            fontFamily='"Inter", sans-serif'
                          >
                            Total Orders
                          </Typography>
                          <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
                            {stats.totalOrders || 0}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={700}
                            fontFamily='"Inter", sans-serif'
                          >
                            Completion Rate
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color="#2E7D32"
                            fontFamily='"Inter", sans-serif'
                          >
                            {stats.totalOrders > 0
                              ? `${((stats.completed || 0) / stats.totalOrders * 100).toFixed(1)}%`
                              : '0%'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: '16px',
                        border: `1px solid ${alpha(currentColor, 0.1)}`,
                        p: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 32px ${alpha(currentColor, 0.08)}`,
                        },
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{ mb: 2, color: currentColor, fontFamily: '"Inter", sans-serif' }}
                      >
                        Status Breakdown
                      </Typography>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              fontFamily='"Inter", sans-serif'
                            >
                              Completed
                            </Typography>
                          </Box>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color="#2E7D32"
                            fontFamily='"Inter", sans-serif'
                          >
                            {stats.completed || 0}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PendingIcon sx={{ color: '#FF9800', fontSize: 20 }} />
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              fontFamily='"Inter", sans-serif'
                            >
                              Pending
                            </Typography>
                          </Box>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color="#FF9800"
                            fontFamily='"Inter", sans-serif'
                          >
                            {stats.pending || 0}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CancelIcon sx={{ color: '#D32F2F', fontSize: 20 }} />
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              fontFamily='"Inter", sans-serif'
                            >
                              Cancelled
                            </Typography>
                          </Box>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            color="#D32F2F"
                            fontFamily='"Inter", sans-serif'
                          >
                            {stats.cancelled || 0}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {activeTab === 2 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={7}>
                    <HorizontalBarChart
                      data={chartData.paymentMethods}
                      locationColor={currentColor}
                      title="Payment Method Breakdown"
                      height={300}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: '16px',
                        border: `1px solid ${alpha(currentColor, 0.1)}`,
                        p: 3,
                        height: '100%',
                        minHeight: 320,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 32px ${alpha(currentColor, 0.08)}`,
                        },
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{ mb: 2, color: currentColor, fontFamily: '"Inter", sans-serif' }}
                      >
                        Payment Summary
                      </Typography>
                      <Stack spacing={2}>
                        {Object.keys(chartData.paymentMethods?.datasets?.[0]?.data || {}).length ===
                        0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                            sx={{ py: 4, fontWeight: 600, fontFamily: '"Inter", sans-serif' }}
                          >
                            No payment data available
                          </Typography>
                        ) : (
                          chartData.paymentMethods.labels?.map((label, index) => {
                            const value =
                              chartData.paymentMethods.datasets?.[0]?.data?.[index] || 0;
                            const colors = [
                              '#4CAF50',
                              '#1976D2',
                              '#9C27B0',
                              '#FF6F00',
                              '#FF9800',
                              '#00BCD4',
                              '#795548',
                            ];
                            return (
                              <Box key={index}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        bgcolor: colors[index % colors.length],
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      fontWeight={700}
                                      fontFamily='"Inter", sans-serif'
                                    >
                                      {label}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight={800}
                                    fontFamily='"Inter", sans-serif'
                                  >
                                    {formatCurrency(value)}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    mt: 0.5,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: alpha('#000', 0.06),
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      borderRadius: 3,
                                      bgcolor: colors[index % colors.length],
                                      width: `${(value / (stats.totalRevenue || 1)) * 100}%`,
                                      transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          })
                        )}
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {activeTab === 3 && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={7}>
                    <GradientAreaChart
                      data={chartData.daily}
                      locationColor={currentColor}
                      title="Daily Revenue Trend"
                      height={300}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <RadarChartComponent
                      data={chartData.hourly}
                      locationColor={currentColor}
                      title="Hourly Order Distribution"
                      height={300}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Orders Table */}
              {isFetching && filteredOrders.length === 0 ? (
                <Box sx={{ py: 4 }}>
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px', mb: 2 }} />
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px', mb: 2 }} />
                  <Skeleton variant="rectangular" height={80} sx={{ borderRadius: '12px' }} />
                </Box>
              ) : filteredOrders.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    bgcolor: 'white',
                    borderRadius: '16px',
                    border: `2px dashed ${alpha('#000', 0.08)}`,
                  }}
                >
                  <ReceiptLongIcon sx={{ fontSize: 72, color: 'text.disabled', opacity: 0.3 }} />
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    sx={{ mt: 2, fontWeight: 700, fontFamily: '"Inter", sans-serif' }}
                  >
                    No Orders Found
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontWeight: 600, fontFamily: '"Inter", sans-serif' }}
                  >
                    Try adjusting your filters or date range
                  </Typography>
                </Box>
              ) : (
                <Card elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
                  <TableContainer>
                    <Table size={isMobile ? 'small' : 'medium'}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(currentColor, 0.04) }}>
                          <TableCell
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '0.6rem', sm: '0.8rem' },
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            Order #
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '0.6rem', sm: '0.8rem' },
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '0.6rem', sm: '0.8rem' },
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            Waiter
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '0.6rem', sm: '0.8rem' },
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            Items
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '0.6rem', sm: '0.8rem' },
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '0.6rem', sm: '0.8rem' },
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            Payment
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '0.6rem', sm: '0.8rem' },
                              fontFamily: '"Inter", sans-serif',
                            }}
                          >
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedOrders.map((order, index) => (
                          <OrderTableRow
                            key={order.id || index}
                            order={order}
                            locationColor={currentColor}
                            onViewDetails={handleViewDetails}
                            index={index}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredOrders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      borderTop: `1px solid ${alpha('#000', 0.06)}`,
                      '& .MuiTablePagination-select': {
                        fontWeight: 700,
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        fontFamily: '"Inter", sans-serif',
                      },
                      '& .MuiTablePagination-displayedRows': {
                        fontWeight: 700,
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        fontFamily: '"Inter", sans-serif',
                      },
                      '& .MuiTablePagination-actions button': {
                        fontSize: { xs: '1.2rem', sm: '1.5rem' },
                      },
                    }}
                  />
                </Card>
              )}
            </Box>
          </div>
        </div>
        <Footer />
      </div>

      {/* Order Detail Dialog */}
      <OrderDetailDialog
        open={detailDialogOpen}
        order={selectedOrder}
        onClose={handleCloseDetails}
        locationColor={currentColor}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            borderRadius: '10px',
            fontWeight: 700,
            fontFamily: '"Inter", sans-serif',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          
          .MuiTableRow-root {
            transition: background-color 0.2s ease;
          }
          
          .MuiTableRow-root:hover {
            background-color: rgba(0,0,0,0.02);
          }
          
          * {
            font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif !important;
          }
          
          .MuiTypography-root {
            font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif !important;
          }
          
          .MuiButton-root {
            font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif !important;
          }
          
          .MuiChip-root {
            font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif !important;
          }
          
          .MuiInputBase-root {
            font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif !important;
          }
          
          .MuiMenuItem-root {
            font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif !important;
          }
          
          .MuiTableCell-root {
            font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif !important;
          }
          
          .MuiFormLabel-root {
            font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif !important;
          }
        `}
      </style>
    </>
  );
};

export default SalesSummary;